import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '@dopomogai/agent-kit';

export type WebsiteV2PageType =
  | 'homepage'
  | 'software_services'
  | 'ai_revenue_audit'
  | 'thank_you';

type WebsiteV2EventName =
  | 'cta_clicked'
  | 'form_started'
  | 'form_submitted'
  | 'widget_opened'
  | 'widget_quick_reply_clicked'
  | 'widget_route_selected'
  | 'widget_handoff_started'
  | 'faq_item_opened'
  | 'thank_you_viewed'
  | 'page_viewed';

type Dict = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const pushDataLayer = (event: string, payload: Dict) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });
};

export const useWebsiteV2Analytics = (
  pageType: WebsiteV2PageType,
  { trackPageView = true }: { trackPageView?: boolean } = {},
) => {
  const analytics = useAnalytics();
  const location = useLocation();

  const track = useCallback(
    (event: WebsiteV2EventName, properties: Dict = {}) => {
      const payload = {
        page_path: location.pathname,
        page_type: pageType,
        ...properties,
      };

      analytics.track(event, payload);
      pushDataLayer(event, payload);
    },
    [analytics, location.pathname, pageType],
  );

  useEffect(() => {
    if (!trackPageView) return;
    track('page_viewed');
  }, [track, trackPageView]);

  return { track };
};
